import { useEffect, useRef, useState } from 'react';

export const useTriggerFocus = (isOpen: boolean) => {
  const triggerBtnRef = useRef<HTMLElement | null>(null);
  const [toggled, setToggled] = useState(false);

  /**
   * focus on trigger button immediately after closing popover or after closing modal if modal opened from popover
   * https://encore.spotify.net/web/components/popover?format=figma-figma#accessibility
   */
  useEffect(() => {
    if (!isOpen && toggled) {
      triggerBtnRef.current?.focus({ preventScroll: true });
    }
  }, [toggled, triggerBtnRef, isOpen]);

  const toggleModal = () => {
    setToggled(true);
  };

  return {
    toggleModal,
    triggerBtnRef,
  };
};

export const useModalPopoverFocus = (isOpen: boolean) => {
  const triggerBtnRef = useRef<HTMLElement | null>(null);
  const firstInteractiveEleRef = useRef<HTMLElement | null>(null);
  const lastInteractiveEleRef = useRef<HTMLElement | null>(null);
  const [toggled, setToggled] = useState(false);

  /**
   * focus on first button immediately after showing popover & focus on trigger button immediately after closing popover
   * https://encore.spotify.net/web/components/popover?format=figma-figma#accessibility
   */
  useEffect(() => {
    if (!isOpen && toggled) {
      triggerBtnRef.current?.focus();
    } else if (isOpen) {
      firstInteractiveEleRef.current?.focus();
    }
  }, [toggled, triggerBtnRef, isOpen]);

  const toggleModal = () => {
    setToggled(true);
  };

  const keyDownHandler = (e: KeyboardEvent) => {
    if (e.key !== 'Tab') return;

    const firstBtn = firstInteractiveEleRef.current;
    const lastBtn = lastInteractiveEleRef.current;

    if (
      !e.shiftKey &&
      (document.activeElement === lastBtn ||
        lastBtn?.contains(e.composedPath()[0] as Node))
    ) {
      firstBtn?.focus();
      e.preventDefault();
    }

    if (
      e.shiftKey &&
      (document.activeElement === firstBtn ||
        firstBtn?.contains(e.composedPath()[0] as Node))
    ) {
      lastBtn?.focus();
      e.preventDefault();
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', keyDownHandler);

    return () => {
      window.removeEventListener('keydown', keyDownHandler);
    };
  });

  return {
    toggleModal,
    triggerBtnRef,
    firstInteractiveEleRef,
    lastInteractiveEleRef,
  };
};
